import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useChargeSessionsStore } from '../store/charge-sessions-store';
import type { Session } from '../types/charge-session.types';

/**
 * Charge-state strings carried by the contract (spec §2). Mapped to i18n
 * labels here; any state not in this set renders raw (see `stateLabel`).
 */
const KNOWN_CHARGE_STATES = [
  'reserved',
  'paying',
  'starting',
  'charging',
  'paused',
  'suspended_ev',
  'suspended_evse',
  'scheduled',
  'stopping',
  'releasing',
] as const;

type KnownChargeState = (typeof KNOWN_CHARGE_STATES)[number];

function isKnownChargeState(state: string): state is KnownChargeState {
  return (KNOWN_CHARGE_STATES as readonly string[]).includes(state);
}

/**
 * Badge color per charge state so the status reads at a glance:
 * green = actively charging, amber = paused / suspended (needs attention),
 * blue = transitional (starting / stopping / releasing / reserved / paying),
 * gray = scheduled or any unknown state.
 */
const STATE_BADGE_CLASS: Record<KnownChargeState, string> = {
  charging: 'bg-green-100 text-green-800',
  paused: 'bg-amber-100 text-amber-800',
  suspended_ev: 'bg-amber-100 text-amber-800',
  suspended_evse: 'bg-amber-100 text-amber-800',
  starting: 'bg-blue-100 text-blue-800',
  stopping: 'bg-blue-100 text-blue-800',
  releasing: 'bg-blue-100 text-blue-800',
  reserved: 'bg-blue-100 text-blue-800',
  paying: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-monta-gray-100 text-monta-gray-700',
};

const DEFAULT_STATE_BADGE_CLASS = 'bg-monta-gray-100 text-monta-gray-700';

/**
 * Detail panel for a selected charge-session bucket (BIZOPS-1588).
 *
 * Desktop: right-hand side panel. Mobile (<800px): bottom sheet. Mirrors the
 * responsive shell used by the charge-point info panel. Purely presentational:
 * data comes from the store (filled by the postMessage bridge in
 * `AppInitializer`, which also requests Tier-2 detail on selection); this
 * component never fetches.
 */
export function ChargeSessionDetailPanel() {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width: 800px)');
  const selectedPointId = useChargeSessionsStore((s) => s.selectedPointId);
  const selectedDetail = useChargeSessionsStore((s) => s.selectedDetail);
  const clearSelection = useChargeSessionsStore((s) => s.clearSelection);

  // Close on ESC, matching the charge-point panel.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearSelection();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [clearSelection]);

  if (!selectedPointId) {
    return null;
  }

  const containerClassName = isDesktop
    ? 'absolute right-0 top-0 z-30 flex h-full w-96 max-w-full flex-col bg-white shadow-monta-lg'
    : 'absolute inset-x-0 bottom-0 z-30 flex max-h-[70vh] flex-col rounded-t-2xl bg-white shadow-monta-lg';

  const isLoading = !selectedDetail;
  const title = selectedDetail?.name ?? t('chargeSessions.detail.loadingTitle');
  const sessions = selectedDetail?.sessions ?? [];

  return (
    <div
      className={containerClassName}
      role="dialog"
      aria-label={t('chargeSessions.detail.title')}
      data-testid="charge-session-detail-panel"
    >
      {/* Header */}
      <div className="flex flex-shrink-0 items-start justify-between border-b border-monta-gray-200 px-4 py-3">
        <div className="flex-1">
          <div className="mb-1 font-mono text-xs text-monta-gray-500">
            {t('chargeSessions.detail.title')}
          </div>
          <h2 className="font-heading text-lg font-bold text-monta-gray-900">
            {title}
          </h2>
        </div>
        <button
          onClick={clearSelection}
          className="-mr-1 -mt-1 flex-shrink-0 rounded-full p-2 transition-colors hover:bg-monta-gray-100"
          aria-label={t('common.close')}
        >
          <svg
            className="h-5 w-5 text-monta-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {isLoading && <DetailSkeleton label={t('common.loading')} />}
        {!isLoading && sessions.length === 0 && (
          <p className="py-8 text-center text-sm text-monta-gray-500">
            {t('chargeSessions.detail.empty')}
          </p>
        )}
        {!isLoading &&
          sessions.map((session) => (
            <SessionRow key={session.sessionId} session={session} />
          ))}
      </div>
    </div>
  );
}

function DetailSkeleton({ label }: { label: string }) {
  return (
    <div
      className="flex items-center justify-center py-8 text-sm text-monta-gray-500"
      aria-live="polite"
    >
      {label}
    </div>
  );
}

function SessionRow({ session }: { session: Session }) {
  const { t } = useTranslation();

  const stateLabel = isKnownChargeState(session.state)
    ? t(`chargeSessions.state.${session.state}`)
    : session.state;

  const stateBadgeClass = isKnownChargeState(session.state)
    ? STATE_BADGE_CLASS[session.state]
    : DEFAULT_STATE_BADGE_CLASS;

  const kwhLabel = t('chargeSessions.detail.kwh', { value: session.kwh });

  return (
    <div
      className="rounded-lg border border-monta-gray-200 p-3"
      data-testid={`charge-session-row-${session.sessionId}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-monta-gray-900">
          {session.chargePointName}
        </span>
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${stateBadgeClass}`}
        >
          {stateLabel}
        </span>
      </div>
      <div className="mt-1 text-xs text-monta-gray-600">{session.evseName}</div>
      <div className="mt-2 flex items-center gap-3 text-sm text-monta-gray-700">
        <span>{kwhLabel}</span>
        {session.costLabel && (
          <span>
            {session.costLabel}
            {session.currency ? ` ${session.currency}` : ''}
          </span>
        )}
      </div>
      {session.consumerLabel && (
        <div className="mt-1 text-xs text-monta-gray-500">
          {session.consumerLabel}
        </div>
      )}
    </div>
  );
}
